
import {debounceTime} from 'rxjs/operators';
import { Subscription ,  Subject ,  forkJoin } from "rxjs";
import { AfterViewInit, Component, OnDestroy, OnInit } from "@angular/core";
import { ListOption } from "../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import { AssigneeFromSearch,
  AssigneeService,
  ListOfAssigneesFromSearch,
  ConfigService,
  RbxRubixConfiguration,
  TaxReturnTypeService,
  TaxReturnSubTypeService,
  TaxReturnType,
  TaxReturnSubType} from "../../../swaggerServices";
import { UserInfoService } from "../user-info.service";
import { ActivatedRoute } from "@angular/router";
import { TaxReturnStatus } from "../tax-returns/tax-return/model/status";
import { initializeTaxReturn } from "../tax-returns/get-full-tax-return";
import { DropdownListOption } from "../../shared/components/search-dropdown/search-dropdown.component";
import { TaxYearsService } from "../../shared/util/tax-years.service";
import { ConfigStateService } from '../config/config-state.service';
import { TenantConfigService } from "../../shared/services/tenant-config.service";
import { Tenant } from '../../shared/types/tenant.enum';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslocoService} from "@jsverse/transloco";

@Component({
    selector: 'app-taxpayers-search',
    templateUrl: './taxpayers-search.component.html',
    styleUrls: ['./taxpayers-search.component.scss'],
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: { scope: 'taxpayers-search', alias: 'taxpayersSearch' },
        }
    ],
    standalone: false
})
export class TaxpayersSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  subscriptions: Subscription[] = [];
  rootForm: UntypedFormGroup;
  assignees: Array<AssigneeFromSearch> | null;
  searchRequestSubscription: Subscription | null;
  taxYearOptions: DropdownListOption[];
  taxReturnStatusOptions: Array<ListOption>;
  searchErrorMessage?: string = null;
  searching = false;
  searchChanged: Subject<{searchText: string, year?: number, status?: number, trType?: number, trSubtype?: number}> =
    new Subject<{searchText: string, year?: number, status?: number, trType?: number, trSubtype?: number}>();
  rubixConfig: RbxRubixConfiguration;
  searchPlaceholder: string;
  taxReturnTypeList: any[] = [];
  taxReturnSubtypeList: any[] = [];
  baseTaxReturnTypeList: any[] = [];
  baseTaxReturnSubtypeList: any[] = [];
  showTaxReturnType = true;
  showQnrType: boolean;
  tenants: any;
  uiConfig: TenantConfigService;
  taxReturnAlias: string;
  yearLabel: string;

  constructor(public userInfo: UserInfoService,
              private assigneeService: AssigneeService,
              private activatedRoute: ActivatedRoute,
              public taxYearsService: TaxYearsService,
              public config: ConfigStateService,
              public configService: ConfigService,
              private taxReturnTypeService: TaxReturnTypeService,
              private taxReturnSubTypeService: TaxReturnSubTypeService,
              private translocoService: TranslocoService,
              ) {
    this.assignees = null;
    this.searchRequestSubscription = null;
    this.taxReturnStatusOptions = [];
    this.rootForm = new UntypedFormGroup({});
  }

  ngOnInit() {
    
    this.showQnrType = this.config.tenant === Tenant.AU;
    const s = this.searchChanged.pipe(
      debounceTime(400))
      .subscribe(({searchText, year, status, trType, trSubtype}) => {
        this.searchAssignees(searchText, year, status, trType, trSubtype);
      });
    this.subscriptions.push(s);

    this.subscriptions.push(this.taxYearsService.getTaxYears(true).subscribe((years: any) => {
      this.taxYearOptions = years;
      const currYr = this.taxYearsService.getCurrentTaxYear();
        this.subscriptions.push(this.activatedRoute.queryParams.subscribe(params => {
          const searchYear = params.year || null;
          if ( searchYear ) {
            this.rootForm.addControl('taxYearForm', new UntypedFormControl("All"));
          } else {
            this.rootForm.addControl('taxYearForm', new UntypedFormControl(currYr));
          }
        }));
        this.subscriptions.push(
          this.config.loadConfig().subscribe(config => {
            this.uiConfig = new TenantConfigService(config);
            this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
            this.yearLabel = this.uiConfig.config.yearLabel;
            this.translocoService.selectTranslate('year').subscribe(value => this.yearLabel = value);
            this.translocoService.selectTranslate('tax-return-alias').subscribe(value => this.taxReturnAlias = value);
          })
        );
    }));
    this.subscriptions.push(this.rootForm.valueChanges.subscribe(() => {
      this.doSearch();
    }));
    this.configService.getConfig().subscribe(() => {
      this.taxReturnStatusOptions = this.makeTaxReturnStatusOptions();
      this.rootForm.addControl('taxReturnStatusForm', new UntypedFormControl({}));
      const tenantConfigService = new TenantConfigService(this.config);
      this.searchPlaceholder = tenantConfigService.config.searchPlaceholder;
      this.translocoService.selectTranslate('search-place-holder').subscribe(value => this.searchPlaceholder = value);
    });

    forkJoin([
      this.taxReturnTypeService.getTaxReturnTypes(),
      this.taxReturnSubTypeService.getTaxReturnSubTypes(),
    ]).subscribe(([types, subtype]: any) => {
      if (types && types.length) {
        this.baseTaxReturnTypeList = types;
        this.populateTrType(this.baseTaxReturnTypeList);
        this.rootForm.addControl('taxReturnTypesForm', new UntypedFormControl({}));
      }
      if (subtype.subtypes && subtype.subtypes.length) {
        this.baseTaxReturnSubtypeList = subtype.subtypes;
        this.populateTrSubtype(this.baseTaxReturnSubtypeList);
        this.rootForm.addControl('taxReturnSubtypesForm', new UntypedFormControl({}));
      }
      this.showTaxReturnType = (types && types.length > 1) || (subtype && subtype.subtypes && subtype.subtypes.length > 1);
    });
    this.tenants = Tenant;
  }

  ngAfterViewInit() {
    // NOTE(mike): setTimeout needed due to child components adding formControls :(
    setTimeout(() => {
      this.subscriptions.push(this.activatedRoute.queryParams.subscribe(params => {
        const searchText = params.q || '';
        if (searchText) {
          this.rootForm.controls.searchText.setValue(searchText);
        }
      }));
    }, 0);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  populateTrType(trTypeList: any) {
    this.taxReturnTypeList = [{
      id: 0,
      name: 'All',
      value: 'All'
    }];
    this.taxReturnTypeList = [...this.taxReturnTypeList, ...trTypeList.map(t => {
      return {
        id: t.id,
        name: t.type,
        value: t.type
      };
    })];
  }

  populateTrSubtype(trSubTypelist: any) {
    this.taxReturnSubtypeList = [{
      id: 0,
      name: 'All',
      value: 'All'
    }];
    this.taxReturnSubtypeList = [...this.taxReturnSubtypeList, ...trSubTypelist.map(t => {
      return {
        id: t.id,
        name: t.subType,
        value: t.subType
      };
    })];
  }

  doSearch() {
    const formData = this.rootForm.value;
    const taxYearDropdown = formData.taxYearForm;
    const year = taxYearDropdown && taxYearDropdown.value || undefined;
    const trStatusDropdown = formData.taxReturnStatusForm;
    let status;
    if ( trStatusDropdown && trStatusDropdown.id && trStatusDropdown.id !== this.taxReturnStatusOptions[0].id ) {
      status = trStatusDropdown.id;
    } else if ( trStatusDropdown && trStatusDropdown.id === 0 ) {
      status = 0;
    }
    const searchText = formData.searchText;
    const trTypeDropdown = formData.taxReturnTypesForm;
    const trSubTypeDropdown = formData.taxReturnSubtypesForm;
    const trType = trTypeDropdown && trTypeDropdown.id && trTypeDropdown.id !== this.taxReturnTypeList[0].id ?
      formData.taxReturnTypesForm.id.toString() : undefined;
    const trSubtype = trSubTypeDropdown && trSubTypeDropdown.id && trSubTypeDropdown.id !== this.taxReturnSubtypeList[0].id ?
      formData.taxReturnSubtypesForm.id.toString() : undefined;
    this.searchChanged.next({searchText, year, status, trType, trSubtype});
  }

  searchAssignees(searchText: string, year?: number, status?: number, trType?: number, trSubtype?: number) {
    const orderBy = undefined;
    const direction = undefined;
    if (searchText) {
      // Cancel a previous subscription before making a new request.
      // A simple way to ensure that responses from previous requests don't accidentally
      // replace responses from newer requests.
      if (this.searchRequestSubscription && !this.searchRequestSubscription.closed) {
        this.searchRequestSubscription.unsubscribe();
      }
      this.searchErrorMessage = null;
      this.searching = true;
      this.searchRequestSubscription = this.assigneeService.getAssigneesBySearchQuery(searchText, orderBy, direction, status, year, trType, trSubtype)
        .subscribe((data: ListOfAssigneesFromSearch) => {
            data.assignees = data.assignees.filter(a => a.taxReturns.length > 0);
            data.assignees.forEach(assignee => assignee.taxReturns.forEach((taxReturn: any) => {
              if (taxReturn.taxReturnTypeId) {
                const found = this.baseTaxReturnTypeList.find(t => t.id === taxReturn.taxReturnTypeId);
                if (found) {
                  taxReturn.taxReturnTypeName = found.type;
                }
              }
              if (taxReturn.taxReturnSubTypeId) {
                const found = this.baseTaxReturnSubtypeList.find(t => t.id === taxReturn.taxReturnSubTypeId);
                if (found) {
                  taxReturn.taxReturnSubTypeName = found.subType;
                }
              }
              initializeTaxReturn(taxReturn);
            }));

            this.searching = false;
            this.assignees = data.assignees || [];
          },
          e => {
            this.searching = false;
            this.searchErrorMessage = 'Search unavailable, please try again later.';
          },
        );
    }
  }

  makeTaxReturnStatusOptions(): Array<DropdownListOption> {
    return [{
      id: -1,
      name: 'All',
      value: 'All',
      order: -1,
    }].concat(TaxReturnStatus.asList(this.config, false).map((opt, i) => ({
      id: opt.id,
      name: opt.name,
      value: opt.name,
      order: i,
    })));
  }
}
